import React, { useState } from "react";
import styles from "./OrderList.module.scss";

import moment from "moment";
import { OrdersProps } from "../../types/orders";
import { useAppDispatch } from "../../app/hooks";
import {
  fetchEditPartyItemsList,
  setEditOrder,
  toggleOrderModal,
  toggleOrderPhotoModal,
  toggleReceiveHistoryModal,
} from "../../features/orders/ordersSlice";
import useConfirmPopup from "../../context/useConfirmPopup";

type Props = {
  orders: OrdersProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeleteOrder: (id: number, password: string) => void;
};

const OrderList = ({
  orders,
  pageNumber,
  pageSize,
  isLoading,
  handleDeleteOrder,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditOrder = async (order: OrdersProps) => {
    await dispatch(fetchEditPartyItemsList(order.party_id));
    await dispatch(setEditOrder(order));
    dispatch(toggleOrderModal(true));
  };

  const handleDeleteRow = async (id: number) => {
    const response = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (response.confirmed) {
      setIsActionLoading(id);
      await handleDeleteOrder(id, response.password);
      setIsActionLoading(0);
    }
  };

  const handleOpenPhoto = async (order: OrdersProps) => {
    dispatch(toggleOrderPhotoModal(order.image));
  };

  const handleOpenReceiveHistory = (order: OrdersProps) => {
    dispatch(toggleReceiveHistoryModal(order));
  };

  const getTotalAmount = (order: OrdersProps) => {
    return (order.qty || 0) * (order.rate || 0);
  };

  return (
    <div className={styles.OrderList}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Sr. No.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    ઓર્ડર ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    પાર્ટી
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    આઈટમ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    નંગ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    પરત નંગ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    પરત વજન
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    ટોટલ રૂ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    સ્ટેટ્સ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    તારીખ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    એકશન્સ બટન
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="text-primary-2 font-bold text-xl text-center py-2"
                    >
                      રાહ જુઓ...
                    </td>
                  </tr>
                ) : (
                  <>
                    {orders.length > 0 ? (
                      <>
                        {orders.map((order, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(pageNumber - 1) * pageSize + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {order.id || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {order?.party?.firm_name || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {order?.item?.name || "-"}
                              {order?.item?.isinset ? (
                                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                  સેટ
                                </span>
                              ) : <></>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {order?.qty || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {order?.item?.isinset ? (
                                <div className="space-y-1">
                                  {(order?.set_delivered_qty || 0) > 0 && (
                                    <div>
                                      <span className="text-gray-500">કેશ: </span>
                                      <span>{order?.set_delivered_qty || 0}</span>
                                    </div>
                                  )}
                                  {(order?.delivered_qty || 0) > 0 && (
                                    <div>
                                      <span className="text-gray-500">પટ્ટા: </span>
                                      <span>{order?.delivered_qty || 0}</span>
                                    </div>
                                  )}
                                  {(order?.delivered_qty || 0) === 0 && (order?.set_delivered_qty || 0) === 0 && "-"}
                                </div>
                              ) : (
                                <span>{order?.delivered_qty || "-"}</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {order?.item?.isinset ? (
                                <div className="space-y-1">
                                  {(order?.set_delivered_qty || 0) > 0 && (
                                    <div>
                                      <span className="text-gray-500">કેશ: </span>
                                      <span>{order?.set_delivered_weight ? order.set_delivered_weight.toFixed(3) : "0.000"}</span>
                                    </div>
                                  )}
                                  {(order?.delivered_qty || 0) > 0 && (
                                    <div>
                                      <span className="text-gray-500">પટ્ટા: </span>
                                      <span>{order?.delivered_weight ? order.delivered_weight.toFixed(3) : "0.000"}</span>
                                    </div>
                                  )}
                                  {(order?.delivered_qty || 0) === 0 && (order?.set_delivered_qty || 0) === 0 && "-"}
                                </div>
                              ) : (
                                <span>{order?.delivered_weight ? order.delivered_weight.toFixed(3) : "-"}</span>
                              )}
                            </td>
                            <td className="text-right px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {getTotalAmount(order).toFixed(
                                2
                              ) || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {order?.status === 1
                                ? "પૂરું કામ"
                                : order?.status === 0
                                  ? "ચાલુ કામ"
                                  : "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {order?.created_at
                                ? moment(order.created_at).format("DD-MM-YYYY hh:mm A")
                                : "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {isActionLoading === order.id ? (
                                <p className="text-primary-2 font-bold text-right text-lg">
                                  રાહ જુઓ...
                                </p>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleOpenPhoto(order)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    Photo
                                  </button>
                                  <button
                                    onClick={() => handleOpenReceiveHistory(order)}
                                    className="text-green-600 hover:text-green-900 ml-4"
                                  >
                                    History
                                  </button>
                                  <button
                                    onClick={() => handleEditOrder(order)}
                                    className="text-indigo-600 hover:text-indigo-900 ml-4"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRow(order.id)}
                                    className="text-red-600 hover:text-red-900 ml-4"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={11}
                          className="text-primary-2 font-bold text-xl text-center py-2"
                        >
                          ઓર્ડર ની વિગતો મળી નથી
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
export default OrderList;
